import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import FormField from '../components/ui/FormField';
import { validate, required, minLength, matches } from '../utils/validation';
import { api } from '../api/client';
import Button from '../components/ui/Button';
import PasswordInput from '../components/PasswordInput';
import '../App.css';
import { translateError } from '../utils/errorMessages';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const passwordError = validate(password, [
      required(t('validation.passwordRequired')),
      minLength(8, t('validation.passwordMinLength'))
    ]);
    if (passwordError) newErrors.password = passwordError;

    const confirmPasswordError = validate(confirmPassword, [
      required(t('validation.confirmPasswordRequired')),
      matches(password, t('common.password'), t('auth.passwordsDoNotMatch'))
    ]);
    if (confirmPasswordError) newErrors.confirmPassword = confirmPasswordError;

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(t('auth.invalidResetToken'));
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await api.auth.resetPassword(token, password);
      setIsSuccess(true);
    } catch (err: any) {
      const message = translateError(err, t, 'auth.invalidResetToken');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <div className="page-content">
        <div className="form-container">
          {tenant.name && (
            <div className="organization-badge">
              {tenant.name}
            </div>
          )}

          <h2>{t('auth.resetPasswordTitle')}</h2>

          <div className="error-message" role="alert">
            {t('auth.invalidResetToken')}
          </div>

          <Link to="/forgot-password" className="form-link">
            {t('auth.forgotPassword')}
          </Link>
          <Link to="/login" className="form-link">
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="form-container">
        {/* Show organization name if on tenant subdomain */}
        {tenant.name && (
          <div className="organization-badge">
            {tenant.name}
          </div>
        )}

        <h2>{t('auth.resetPasswordTitle')}</h2>

        {isSuccess ? (
          <>
            <div className="success-message" role="status">
              {t('auth.passwordResetSuccess')}
            </div>
            <Link to="/login" className="form-link">
              {t('auth.backToLogin')}
            </Link>
          </>
        ) : (
          <>
            <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary, #aaa)' }}>
              {t('auth.resetPasswordDescription')}
            </p>

            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={submitHandler}>
              <FormField
                htmlFor="password"
                label={t('auth.newPassword')}
                error={fieldErrors.password}
                required
              >
                <PasswordInput
                  id="password"
                  placeholder={t('auth.enterPassword')}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: '' }));
                  }}
                  disabled={isLoading}
                />
              </FormField>
              <FormField
                htmlFor="confirmPassword"
                label={t('auth.confirmNewPassword')}
                error={fieldErrors.confirmPassword}
                required
              >
                <PasswordInput
                  id="confirmPassword"
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (fieldErrors.confirmPassword) setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
                  }}
                  disabled={isLoading}
                />
              </FormField>
              <Button type="submit" fullWidth loading={isLoading}>
                {isLoading ? t('common.loading') : t('auth.resetPasswordTitle')}
              </Button>
            </form>

            <Link to="/forgot-password" className="form-link">
              {t('auth.forgotPassword')}
            </Link>
            <Link to="/login" className="form-link">
              {t('auth.backToLogin')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
